import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ApiResponse } from "@entrilia/shared/api/types";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../../../api/adminApi";
import type { WorkflowActivity, WorkflowActivityChangeSet } from "../../../../../../api/types/workflowTypes";
import ObjectAutocomplete from "./ObjectAutocomplete";
import { TransactionEditContextProvider } from "./TransactionEditContext";
import type { TransactionRelatedEntities } from "./transactionEditState";
import {
  fillTransactionRelatedEntitiesAction,
  getInitialTransactionEditState,
  mapStateToUpdateWorkflowActivityChangeSetsRequest,
  updateEntityGroupAction,
  updateIsMultiEntityAction,
  validateTransactionEditState,
} from "./transactionEditState";
import TransactionLinesTable from "./TransactionLinesTable";
import TransactionRelatedEntitiesTable from "./TransactionRelatedEntitiesTable";

interface Props {
  workflowId: string;
  activityId: string;
  activityChangeSet: WorkflowActivityChangeSet;
  onClose: () => void;
  onSaved: (updatedActivity: WorkflowActivity) => void;
}

const loadAllRelatedEntities = async (): Promise<ApiResponse<TransactionRelatedEntities>> => {
  const [portfolioCompaniesResp, entitiesResp, entityGroupsResp, entityGroupAssignmentsResp] = await Promise.all([
    adminApi.searchObjects("PortfolioCompany", { fieldIds: ["Name", "No"] }),
    adminApi.searchObjects("Entity", { fieldIds: ["Name", "No", "CurrencyCode"] }),
    adminApi.searchObjects("EntityGroup", { fieldIds: ["Name", "Code"] }),
    adminApi.searchObjects("EntityGroupAssignment", {}),
  ]);

  if (!portfolioCompaniesResp.success) {
    return { success: false, error: portfolioCompaniesResp.error };
  }
  if (!entitiesResp.success) {
    return { success: false, error: entitiesResp.error };
  }
  if (!entityGroupsResp.success) {
    return { success: false, error: entityGroupsResp.error };
  }
  if (!entityGroupAssignmentsResp.success) {
    return { success: false, error: entityGroupAssignmentsResp.error };
  }

  return {
    success: true,
    data: {
      portfolioCompanies: portfolioCompaniesResp.data.items,
      entities: entitiesResp.data.items,
      entityGroups: entityGroupsResp.data.items,
      entityGroupAssignments: entityGroupAssignmentsResp.data.items,
    },
  };
};

const updateWorkflowActivityChangeSets = withErrorHandling(adminApi.updateWorkflowActivityChangeSets);

const TransactionEditDialog = ({ workflowId, activityId, activityChangeSet, onClose, onSaved }: Props) => {
  const { sendNotificationError } = useNotificationContext();

  const [editState, setEditState] = useState(() => getInitialTransactionEditState(activityChangeSet));
  const [saving, setSaving] = useState(false);

  const [allRelatedEntities, fetchError] = useFetch(loadAllRelatedEntities, {
    onSetData: (data) => setEditState(fillTransactionRelatedEntitiesAction(activityChangeSet, data)),
  });

  const handleSave = async () => {
    const validationResults = validateTransactionEditState(editState);

    setEditState((state) => ({ ...state, validationResults }));

    if (!validationResults.isValid) {
      sendNotificationError("Please check validation errors");
      return;
    }

    setSaving(true);

    const payload = mapStateToUpdateWorkflowActivityChangeSetsRequest(editState, activityChangeSet);

    const [updatedActivity, error] = await updateWorkflowActivityChangeSets(workflowId, activityId, payload);

    setSaving(false);

    if (error) {
      logError(error, "[TransactionEditDialog] updateWorkflowActivityChangeSets");
      sendNotificationError(error.message);
      return;
    }

    onSaved(updatedActivity);
    onClose();
  };

  if (fetchError) {
    logError(fetchError, "[TransactionEditDialog]");
  }

  const invalidFields = editState.validationResults?.invalidFields ?? [];

  const loading = allRelatedEntities === undefined;

  return (
    <TransactionEditContextProvider
      activityChangeSet={activityChangeSet}
      allEntities={allRelatedEntities?.entities ?? []}
      editState={editState}
      setEditState={setEditState}
    >
      <Dialog open maxWidth="xl" fullWidth>
        <DialogTitle>Edit Transaction</DialogTitle>
        <DialogCloseButton onClick={onClose} />
        <Divider />

        <DialogContent sx={{ height: "80vh" }}>
          {fetchError && <DataLoadingFailed title="Failed to fetch some of the required entities" sx={{ mb: 2 }} />}

          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 3 }}>
              General
            </Typography>
            <Stack direction="row" spacing={4} sx={{ width: "100%" }}>
              <Stack spacing={2} sx={{ width: "100%" }}>
                <ObjectAutocomplete
                  disabled={!allRelatedEntities?.portfolioCompanies.length}
                  options={allRelatedEntities?.portfolioCompanies ?? []}
                  value={editState.investmentCompany}
                  onChange={(investmentCompany) => setEditState((state) => ({ ...state, investmentCompany }))}
                  label={loading ? "Loading..." : "Investment"}
                  error={invalidFields.includes("investmentCompany")}
                />

                <TextField
                  fullWidth
                  label="Description"
                  value={editState.description}
                  onChange={(e) => setEditState((state) => ({ ...state, description: e.target.value }))}
                />

                <DatePicker
                  value={editState.postingDate}
                  onChange={(date) => setEditState((state) => ({ ...state, postingDate: date }))}
                  sx={{ width: "24rem" }}
                  slotProps={{
                    textField: {
                      label: "Posting Date",
                      error: invalidFields.includes("postingDate"),
                    },
                  }}
                />
              </Stack>

              <Divider orientation="vertical" flexItem />

              <Stack spacing={2} sx={{ width: "100%" }}>
                <FormControlLabel
                  sx={{ width: "24rem", justifyContent: "flex-end" }}
                  label="Multi Entity"
                  labelPlacement="start"
                  control={
                    <Checkbox
                      checked={editState.isMultiEntity}
                      onChange={(_, checked) => setEditState(updateIsMultiEntityAction(checked))}
                    />
                  }
                />

                {editState.isMultiEntity && (
                  <ObjectAutocomplete
                    disabled={!allRelatedEntities?.entityGroups.length}
                    options={allRelatedEntities?.entityGroups ?? []}
                    value={editState.entityGroup}
                    onChange={(entityGroup) =>
                      setEditState(updateEntityGroupAction(entityGroup, allRelatedEntities?.entities ?? []))
                    }
                    label={loading ? "Loading..." : "Entity Group"}
                    error={invalidFields.includes("entityGroup")}
                  />
                )}

                {!editState.isMultiEntity && (
                  <ObjectAutocomplete
                    disabled={!allRelatedEntities?.entities.length}
                    options={allRelatedEntities?.entities ?? []}
                    value={editState.entity}
                    onChange={(entity) =>
                      setEditState((state) => ({
                        ...state,
                        entity,
                        fundingEntity: entity ? { ...entity } : null,
                      }))
                    }
                    label={loading ? "Loading..." : "Entity"}
                    error={invalidFields.includes("entity")}
                  />
                )}

                {!editState.isMultiEntity && (
                  <ObjectAutocomplete
                    disabled={!allRelatedEntities?.entities.length}
                    options={allRelatedEntities?.entities ?? []}
                    value={editState.fundingEntity}
                    onChange={(fundingEntity) => setEditState((state) => ({ ...state, fundingEntity }))}
                    label={loading ? "Loading..." : "Funding Entity"}
                    error={invalidFields.includes("fundingEntity")}
                  />
                )}
              </Stack>
            </Stack>
          </Paper>

          {editState.isMultiEntity && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 3 }}>
                Entities
              </Typography>
              <TransactionRelatedEntitiesTable loading={loading} />
            </Paper>
          )}

          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 3 }}>
              Lines
            </Typography>
            <TransactionLinesTable />
          </Paper>
        </DialogContent>

        <Divider />

        <DialogButtons onCancel={onClose}>
          <Button color="primary" variant="contained" onClick={handleSave} loading={saving} disabled={loading}>
            Save
          </Button>
        </DialogButtons>
      </Dialog>
    </TransactionEditContextProvider>
  );
};

export default TransactionEditDialog;
